/*
 * Industry content — `lt` (Lithuanian).
 *
 * Lithuanian translation of the international `en` source variant: generic
 * time-registration framing, no belgium-specific pains/features, and
 * `compliance.slug` / `relatedArticles` pointing only at articles that exist
 * under `/en/` (the 4 market-agnostic posts). Pure data (NFR10).
 */
import type { Industry } from "./types";

export const INDUSTRIES_LT: Industry[] = [
  {
    slug: "construction",
    name: "Statyba",
    headline: "Darbo laiko apskaita statybų komandoms — registracija su vietos patvirtinimu, projekto valandos ir biudžeto kontrolė vienoje programėlėje",
    intro: "Komandos išsibarsčiusios po objektus, valandos užrašytos ant popieriaus, subrangovų valandų neįmanoma suderinti. Done-it suteikia kiekvienam objektui sandarų skaitmeninį darbo laiko žiniaraštį, kuris vienu paspaudimu pasiekia Jūsų darbo užmokesčio sistemą.",
    pains: [
      {
        title: "Nėra patikimo įrašo, kas kur dirbo",
        body: "Su keliais aktyviais objektais ir besikeičiančiomis komandomis rankiniai žiniaraščiai klaidingi iš prigimties. Darbuotojai suapvalina, pamiršta įrašus arba užpildo juos po kelių dienų — palikdami Jus su darbo užmokesčio skaičiais, kurių negalite apginti.",
      },
      {
        title: "Biudžeto viršijimai išryškėja per vėlai",
        body: "Kol pastebite, kad objektas išnaudojo planuotas valandas, marža jau dingusi. Nėra atskaitos realiu laiku — tik nemaloni staigmena išrašant sąskaitą.",
      },
      {
        title: "Darbo užmokesčio paruošimas suvalgo valandas kiekvieną mėnesį",
        body: "Žiniaraščių rinkimas, jų suderinimas su projektais ir darbo užmokesčio eksporto paruošimas užtrunka dienas. Kiekvienas įrašas yra potenciali klaida.",
      },
    ],
    features: [
      {
        title: "Registracija su vietos patvirtinimu kiekviename objekte",
        body: "Vienas prisilietimas užregistruoja darbuotoją su vietos patvirtinimu ir susieja valandas su tinkamu projektu. Jokių kortelių skaitytuvų ar terminalų — tik išmanusis telefonas, kurį Jūsų komanda jau turi.",
      },
      {
        title: "Likusios valandos pagal projektą realiu laiku",
        body: "Atidarydami projektą nustatykite planuojamas valandas kaip biudžetą. Darbuotojai mato likusio laiko atskaitą; Jūs matote tai realiu laiku savo skydelyje. Biudžeto viršijimai matomi anksčiau, nei įvyksta.",
      },
      {
        title: "Subrangovų valdymas ir buvimas",
        body: "Pridėkite subrangovus prie projekto, priskirkite juos registracijai ir sekite jų valandas kartu su savo komanda — toje pačioje apžvalgoje.",
      },
      {
        title: "Darbo užmokesčio eksportas vienu paspaudimu",
        body: "Visos registruotos valandos — pagal darbuotoją, pagal projektą — eksportuojamos į švarią skaičiuoklę Jūsų darbo užmokesčio paslaugų teikėjui. Jokio rankinio paruošimo, jokių klaidų.",
      },
    ],
    compliance: {
      label: "Skaitmeninė darbo laiko registracija",
      title: "Registracija su vietos patvirtinimu, sukurta statybvietėms",
      body: "Done-it suteikia registraciją su vietos patvirtinimu, darbo laiko apskaitą pagal projektą ir švarų darbo užmokesčio eksportą statybų komandoms. Komandoms, dirbančioms Belgijos objektuose, jis taip pat automatiškai perduoda registraciją atvykstant į darbą.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Komanda atvyksta į objektą", body: "Kiekvienas darbuotojas paliečia vieną kartą, kad užsiregistruotų. GPS patvirtina vietą, pažymi įrašo laiką ir susieja valandas su projektu. Jokio popieriaus, jokio terminalo." },
      { step: "Matote viską realiu laiku", body: "Valandos, likęs biudžetas, subrangovų buvimas ir bet kokie iš objekto praneši papildomi darbai — viskas Jūsų skydelyje, realiu laiku." },
      { step: "Mėnesio pabaiga per minutes", body: "Eksportuokite visas valandas pagal darbuotoją ir projektą. Siųskite savo darbo užmokesčio paslaugų teikėjui. Done-it automatiškai generuoja švarias, auditui paruoštas ataskaitas." },
    ],
    stats: [
      { value: "1 prisilietimas", label: "kad užsiregistruotumėte bet kuriame objekte — be terminalo, be popieriaus" },
      { value: "1 paspaudimas", label: "darbo užmokesčio eksportas paruoštas Jūsų paslaugų teikėjui" },
      { value: "Realiu laiku", label: "likusių valandų rodinys pagal projektą ir objektą" },
    ],
    quote: {
      text: "Mūsų komanda registruojasi pati objekte. Mėnesio pabaigoje darbo užmokestis tiesiog susidėlioja — visiškai nustojome vaikytis žiniaraščių.",
      who: "Savininkas, statybų įmonė · 14 darbuotojų",
      result: "5 valandos per savaitę sutaupytos administracijai",
    },
    relatedSlugs: ["installation", "maintenance", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "cleaning",
    name: "Valymas",
    headline: "Darbo laiko apskaita valymo įmonėms — registracija su vietos patvirtinimu, buvimo įrodymas ir darbo užmokestis vienoje programėlėje",
    intro: "Valytojai visą dieną juda tarp vietų. Done-it seka, kas kur yra, realiu laiku — registracija su vietos patvirtinimu kiekviename objekte, automatinis pertraukų atėmimas ir švarus darbo užmokesčio eksportas mėnesio pabaigoje.",
    pains: [
      {
        title: "Buvimo įrodymas kiekvienoje vietoje",
        body: "Klientai ir inspektoriai tikisi sandarių lankomumo įrašų. Rankiniai lapai gali būti ginčijami; skaitmeninės registracijos su vietos patvirtinimu — ne.",
      },
      {
        title: "Komandos išsibarsčiusios po daugelį vietų",
        body: "Valytojams visą dieną judant tarp objektų, Jūs neturite vaizdo realiu laiku, kas kur dirba — kol kažkas paskambina.",
      },
      {
        title: "Darbo užmokesčio klaidos dėl suapvalintų valandų",
        body: "Be tikslaus atvykimo ir išvykimo laiko darbo užmokestis atkuriamas iš atminties. Valandos suapvalinamos į viršų, sąnaudos didėja ir kyla ginčai.",
      },
    ],
    features: [
      {
        title: "Registracija ir išsiregistravimas realiu laiku",
        body: "Darbuotojai registruojasi ir išsiregistruoja telefonu kiekvienoje vietoje su vietos patvirtinimu; laikas tikslus, su pertraukų registracija.",
        plan: "Time Basic",
      },
      {
        title: "Automatinis pertraukų atėmimas",
        body: "Privalomos pertraukos automatiškai atimamos iš darbo dienos — jokios rankinės matematikos, jokių ginčų.",
        plan: "Time Basic",
      },
      {
        title: "Daugelio vietų apžvalga realiu laiku",
        body: "Matykite, kurie valytojai yra kuriose vietose šiuo metu. Pastebėkite spragas anksčiau už klientus.",
      },
      {
        title: "Nebuvimo registracija su skaitmeniniais nedarbingumo pažymėjimais",
        body: "Darbuotojai įkelia nedarbingumo pažymėjimus ir nebuvimo dokumentus tiesiai iš programėlės. Sukurkite nebuvimo tipus, kuriuos Jūsų komanda iš tikrųjų naudoja.",
      },
      {
        title: "Darbo užmokesčio eksportas vienu paspaudimu",
        body: "Tikslūs laiko žymos teka tiesiai į darbo užmokesčiui paruoštą eksportą Jūsų paslaugų teikėjui. Jokio perrašymo, jokių klaidų.",
      },
    ],
    compliance: {
      label: "Lankomumas su vietos patvirtinimu",
      title: "Lankomumas realiu laiku kiekvienoje valymo vietoje",
      body: "Done-it suteikia valymo komandoms registraciją su vietos patvirtinimu kiekvienoje vietoje, automatinį pertraukų atėmimą ir vaizdą realiu laiku, kas kuriame objekte yra šiuo metu.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Valytojas atvyksta į vietą", body: "Vienas prisilietimas juos užregistruoja. GPS registruoja objektą ir pažymi įrašo laiką. Pertraukų registracija įmontuota programėlėje." },
      { step: "Matote kiekvieną vietą realiu laiku", body: "Kas kuriame objekte yra šiuo metu — jokių skambučių, jokių spėliojimų. Pastebėkite aprėpties spragas anksčiau už klientus." },
      { step: "Darbo užmokestis vienu paspaudimu", body: "Visi lankomumo duomenys — tikslus laikas, vietos ir pertraukos — švariai eksportuojami Jūsų darbo užmokesčio paslaugų teikėjui." },
    ],
    stats: [
      { value: "1 prisilietimas", label: "kad užsiregistruotumėte bet kurioje vietoje — terminalo nereikia" },
      { value: "Realiu laiku", label: "vaizdas, kas kuriame objekte yra šiuo metu" },
      { value: "0", label: "dvigubų įrašų — valandos teka tiesiai į darbo užmokestį" },
    ],
    quote: {
      text: "Pagaliau galime įrodyti, kas kur ir kada valė. Jokių daugiau lankomumo lapų, jokių klientų ginčų — GPS tuo pasirūpina.",
      who: "Vadovas, valymo įmonė · 9 darbuotojai",
      result: "Jokio valandų apvalinimo į viršų",
    },
    relatedSlugs: ["maintenance", "field-service", "construction"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "installation",
    name: "Montavimas",
    headline: "Darbo laiko apskaita montavimo komandoms — projekto valandos, navigacija ir biudžeto kontrolė vienoje programėlėje",
    intro: "Siųskite savo montuotojus tinkamu adresu su viskuo, ko jiems reikia, automatiškai susiekite kiekvieną dirbtą valandą su tinkamu darbu ir matykite likusį biudžetą prieš objektui užsidarant. Done-it yra paprasčiausias būdas išlaikyti montavimo projektus laiku ir pagal sąnaudas.",
    pains: [
      {
        title: "Darbo informacija nepasiekia lauko komandos laiku",
        body: "Komandos atvyksta į objektą be adreso, kontaktinio asmens ar apimties detalių. Tas pirmasis skambutis yra dešimt minučių apmokestinamo laiko, prarasto dar prieš dirbant vieną valandą.",
      },
      {
        title: "Valandos nesusietos su tinkamu projektu",
        body: "Kai darbuotojai dienos pabaigoje pildo valandas rankiniu būdu — jei iš viso pildo — valandos patenka į netinkamą darbą arba visai dingsta. Sąskaitos neatitinka realybės.",
      },
      {
        title: "Biudžeto viršijimai pastebimi per vėlai",
        body: "Be likusių valandų atskaitos realiu laiku pagal projektą sužinote, kad darbas viršijo biudžetą, uždarydami sąskaitą — ne tada, kai dar galite reaguoti.",
      },
      {
        title: "Subrangovų valandas sunku sekti",
        body: "Subrangovai, dirbantys tame pačiame objekte, naudoja skirtingas sistemas, pildo valandas atskirai arba visai jų nepildo. Suderinimas mėnesio pabaigoje yra lėtas, klaidoms linkęs procesas.",
      },
    ],
    features: [
      {
        title: "Projekto informacija dalijama tiesiai į programėlę",
        body: "Darbuotojai atidaro projektą telefone: adresas, planuojamos valandos, kontaktinis asmuo ir įkelti failai (planai, instrukcijos, PDF). Viskas, ko jiems reikia, yra ten dar prieš atvykstant.",
        plan: "Project Basic",
      },
      {
        title: "Navigacija į objektą vienu mygtuku",
        body: "Paliečiate vieną kartą ir telefonas naviguoja projekto adresu — įmontuota programėlėje, atskiros žemėlapių programėlės nereikia.",
      },
      {
        title: "Likusių valandų atskaita pagal projektą",
        body: "Atidarydami projektą nustatykite planuojamas valandas. Darbuotojai mato, kiek valandų liko; Jūs matote būseną realiu laiku savo skydelyje. Viršijimai matomi anksčiau, nei įvyksta.",
        plan: "Project Basic",
      },
      {
        title: "Darbuotojų registracija pagal projektą",
        body: "Kiekvienas darbuotojas registruojasi tinkamame projekte. Visada žinote, kas kuriame objekte yra šiuo metu.",
        plan: "Project Basic",
      },
      {
        title: "Papildomų darbų ir medžiagų registravimas",
        body: "Kai objekte plečiasi apimtis, darbuotojai registruoja papildomus darbus ir medžiagas realiu laiku — su tekstu ir nuotraukomis — kad niekas nebūtų apmokestinta iš atminties.",
        plan: "Project Basic",
      },
    ],
    compliance: {
      label: "Skaitmeninė darbo laiko registracija",
      title: "Registracija su vietos patvirtinimu pagal projektą montavimo komandoms",
      body: "Done-it suteikia montavimo komandoms registraciją su vietos patvirtinimu pagal projektą, likusių valandų atskaitą realiu laiku ir automatinį darbo užmokesčio eksportą — atitinkant bet kokį vietinį darbo laiko registracijos reikalavimą. Komandoms, dirbančioms Belgijos statybvietėse, jis taip pat integruojasi su CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Darbuotojas atidaro projektą telefone", body: "Adresas, kontaktas, apimtis ir įkelti failai yra čia pat. Jie naviguoja vienu prisilietimu — jokių skambučių į biurą dėl nurodymų." },
      { step: "Valandos automatiškai registruojamos tinkamam darbui", body: "Registracija susieja kiekvieną dirbtą minutę su tinkamu projektu. Likęs biudžetas atsinaujina realiu laiku." },
      { step: "Papildomi darbai fiksuojami objekte", body: "Apimties pakeitimai, papildomos medžiagos ir vėlavimai registruojami su tekstu ir nuotraukomis tą akimirką, kai įvyksta — paruošti sąskaitai." },
    ],
    stats: [
      { value: "Realiu laiku", label: "likusių valandų rodinys pagal projektą" },
      { value: "0", label: "skambučių į biurą dėl objektų adresų" },
      { value: "1 paspaudimas", label: "naviguokite bet kuriuo projekto adresu iš programėlės" },
    ],
    quote: {
      text: "Valandos automatiškai patenka į tinkamą projektą. Darbo sąnaudų apskaita staiga tampa lengva — matome realią maržą prieš uždarydami darbą.",
      who: "Savininkas, montavimo įmonė · 6 darbuotojai",
      result: "Realios sąnaudos pagal darbą, realiu laiku",
    },
    relatedSlugs: ["construction", "electricians", "hvac"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },

  {
    slug: "field-service",
    name: "Lauko paslaugos",
    headline: "Darbo laiko apskaita lauko paslaugų komandoms — kilometražas, išlaidos ir valandos iš kelio, viskas vienoje programėlėje",
    intro: "Technikai, lankantys kelis klientus per dieną, kvitai pirštinių dėtuvėse, kilometražas registruotas iš atminties. Done-it fiksuoja valandas, kilometrus ir išlaidas vietoje — susietus su tinkamu darbu tą akimirką, kai jie įvyksta.",
    pains: [
      {
        title: "Kilometražas registruojamas po kelių dienų — jei iš viso",
        body: "Prašyti technikų savaitės pabaigoje atkurti savo maršrutus reiškia netikslias kompensacijas, ginčus su klientais ir augančią krūvą nepareikalautų kilometrų.",
      },
      {
        title: "Išlaidos ant popierinių kvitų, kurie dingsta",
        body: "Kvitas pirštinių dėtuvėje yra sąnauda, kuri gali niekada nepatekti į sąskaitą. Reali sąnauda pagal darbą visada nepakankamai įvertinta.",
      },
      {
        title: "Nėra vaizdo realiu laiku, kur yra komanda",
        body: "Be registracijų realiu laiku sužinote, kur technikas yra, tik kai kažkas nutinka ir kažkas paskambina.",
      },
      {
        title: "Sunku matyti faktinę maržą pagal darbą",
        body: "Kai darbas, kelionės ir išlaidos sekamos atskirose sistemose — arba visai nesekamos — niekada nėra vienos sąnaudų sumos pagal darbą.",
      },
    ],
    features: [
      {
        title: "Automatinis kilometražo fiksavimas (Trax-it)",
        body: "GPS registruoja išvykimą, atvykimą ir tikslų atstumą pagal kelionę. Konfigūruokite transporto tipus (nuosavas automobilis, įmonės transportas, bendra kelionė) su jų kompensacijos tarifais. Jokio rankinio kelionių žurnalo.",
        plan: "Add-on",
      },
      {
        title: "Išlaidų valdymas objekte",
        body: "Darbuotojai nufotografuoja kvitą vietoje; jis susiejamas su darbu ir paruoštas kompensavimui — o ne vejamasis mėnesio pabaigoje.",
        plan: "Time Basic",
      },
      {
        title: "Su projektu susieta sąnaudų kontrolė",
        body: "Valandos, kilometražas ir išlaidos priskiriamos darbui realiu laiku. Sąnauda pagal darbą matoma, kol darbas dar atviras.",
        plan: "Project Basic",
      },
      {
        title: "Registracija su vietos patvirtinimu ir komandos apžvalga realiu laiku",
        body: "Kiekvienas technikas registruojasi kliento objekte. Matote, kas kur yra, šiuo metu — jokių skambučių, jokių spėliojimų.",
      },
      {
        title: "Darbo užmokesčio eksportas vienu paspaudimu",
        body: "Valandos ir kilometražo kompensacijos švariai eksportuojamos Jūsų darbo užmokesčio paslaugų teikėjui — su automatiškai pritaikytais Jūsų sukonfigūruotais kilometražo tarifais.",
      },
    ],
    compliance: {
      label: "Skaitmeninė laiko ir kilometražo apskaita",
      title: "GPS valandos ir automatinis kilometražas lauko paslaugų komandoms",
      body: "Done-it automatiškai fiksuoja kilometražą pagal kelionę, pritaiko Jūsų sukonfigūruotus kompensacijos tarifus ir registruoja kiekvieną kilometrą tinkamam klientui ar projektui. ES Tyco sprendimas reiškia, kad kelionė iš namų pas pirmąjį klientą skaičiuojama kaip darbo laikas — Done-it tai taip pat seka.",
      slug: "how-to-track-mileage-for-field-teams",
    },
    workflow: [
      { step: "Technikas išvyksta į pirmąjį iškvietimą", body: "Registracija pradeda kelionės žurnalą. Kilometrai automatiškai fiksuojami nuo išvykimo iki atvykimo — be rankinio įvedimo." },
      { step: "Darbo vietoje", body: "Valandos susiejamos su tinkamu projektu. Papildomi darbai, medžiagos ir kvitai registruojami programėlėje tą akimirką, kai įvyksta." },
      { step: "Mėnesio pabaiga be chaoso", body: "Kiekviena valanda, kilometras ir išlaidos jau susieti su tinkamu darbu. Eksportuokite į darbo užmokestį ir išrašykite sąskaitą su realiais sąnaudų skaičiais." },
    ],
    stats: [
      { value: "Automatinis", label: "kilometražo fiksavimas — be rankinio žurnalo" },
      { value: "Realiu laiku", label: "sąnauda pagal darbą: valandos + kelionės + išlaidos" },
      { value: "0", label: "prarastų kilometrų — kiekviena kelionė fiksuojama automatiškai" },
    ],
    quote: {
      text: "Kilometražas anksčiau buvo mėnesinis galvos skausmas. Dabar jis registruojasi pats — kiekvienas kilometras susiejamas su tinkamu klientu dar prieš mums grįžtant į biurą.",
      who: "Operacijų vadovas, lauko paslaugų įmonė · 11 darbuotojų",
      result: "Tiksli sąnauda pagal darbą kiekvieną kartą",
    },
    relatedSlugs: ["hvac", "maintenance", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "hvac",
    name: "ŠVOK",
    headline: "Darbo laiko apskaita ŠVOK technikams — darbo sąnaudų apskaita, dalys ir kelionės vienoje programėlėje",
    intro: "Kiekvienas aptarnavimo iškvietimas turi tris sąnaudas: darbą, dalis ir keliones. Done-it fiksuoja visas tris vietoje ir priskiria jas tinkamam darbui — kad žinotumėte savo maržą prieš uždarydami sąskaitą.",
    pains: [
      {
        title: "Aptarnavimo iškvietimus sunku tiksliai įvertinti",
        body: "Darbas, dalys ir kelionės gyvena skirtingose sistemose — arba skirtingose kišenėse. Reali iškvietimo sąnauda paaiškėja tik gerokai po to, kai technikas paliko objektą.",
      },
      {
        title: "Nėra aptarnavimo laiko įrodymo",
        body: "Kai klientas ginčija, kiek truko remontas, popierinį žiniaraštį lengva užginčyti. GPS patvirtintos registracijos ir išsiregistravimo laiko žymos — ne.",
      },
      {
        title: "Priežiūros sutartys tyliai mažina maržą",
        body: "Pasikartojančiose priežiūros sutartyse maži viršijimai kaupiasi per daugelį vizitų. Be valandų skaitiklio realiu laiku sužinote, kad sutartis nuostolinga, tik atnaujinimo metu.",
      },
      {
        title: "Technikai pamiršta registruoti pertraukas",
        body: "Rankiniai laiko įrašai praleidžia pertraukas, todėl permokama ir darbo užmokestis neatitinka realybės.",
      },
    ],
    features: [
      {
        title: "Projektu pagrįsta darbo laiko apskaita",
        body: "Kiekvienas aptarnavimo iškvietimas ar priežiūros sutartis yra projektas. Valandos, kelionės ir medžiagos priskiriamos realiu laiku — suteikdamos maržos matomumą realiu laiku.",
        plan: "Project Basic",
      },
      {
        title: "Papildomi darbai ir medžiagos, registruojami objekte",
        body: "Kai darbas išauga, technikai registruoja papildomus darbus ir dalis su tekstu bei nuotraukomis prieš išvykdami. Niekas neapmokestinama iš atminties.",
        plan: "Project Basic",
      },
      {
        title: "Kilometražas ir transporto sąnaudos",
        body: "Kilometrai pagal iškvietimą registruojami automatiškai ir įvertinami darbui. Konfigūruokite kompensacijos tarifus pagal transporto tipą.",
        plan: "Add-on",
      },
      {
        title: "Automatinis pertraukų atėmimas",
        body: "Privalomos pertraukos automatiškai atimamos iš bendro laiko — jokių ginčų, jokių darbo užmokesčio klaidų.",
      },
      {
        title: "Registracija su vietos patvirtinimu ir buvimo įrodymas",
        body: "Laiku pažymėta, GPS patvirtinta registracija kiekviename objekte. Neginčijamas įrodymas, kada technikas atvyko ir išvyko.",
      },
    ],
    compliance: {
      label: "Skaitmeninė laiko ir sąnaudų apskaita",
      title: "Pilna darbo sąnaudų apskaita ŠVOK aptarnavimo iškvietimams",
      body: "Done-it suteikia ŠVOK komandoms registraciją su vietos patvirtinimu pagal aptarnavimo iškvietimą, sąnaudų matomumą realiu laiku pagal darbą (darbas + kelionės + dalys) ir švarų darbo užmokesčio eksportą — atitinkant bet kokį vietinį darbo laiko registracijos reikalavimą be papildomos įrangos.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Technikas užsiregistruoja objekte", body: "GPS pažymi atvykimo laiką ir susieja valandas su aptarnavimo iškvietimu ar priežiūros sutartimi. Jokio popieriaus, jokio terminalo." },
      { step: "Papildomi darbai fiksuojami vietoje", body: "Panaudotos dalys, papildomos valandos ir apimties pakeitimai registruojami programėlėje su nuotraukomis — prieš paliekant objektą." },
      { step: "Darbo sąnauda matoma iškart", body: "Darbas, kelionės ir medžiagos sumuojami projektui realiu laiku. Matote maržą prieš išrašydami sąskaitą." },
    ],
    stats: [
      { value: "3", label: "sąnaudų tipai fiksuojami pagal iškvietimą: darbas, kelionės, dalys" },
      { value: "Realiu laiku", label: "maržos matomumas pagal aptarnavimo sutartį" },
      { value: "0", label: "staigmenų išrašant sąskaitą — sąnaudos matomos realiu laiku" },
    ],
    quote: {
      text: "Pagaliau žinome, kiek mums iš tikrųjų kainuoja kiekviena aptarnavimo sutartis. Maržos staigmenos baigėsi tą dieną, kai pradėjome naudoti Done-it.",
      who: "Savininkas, ŠVOK įmonė · 7 technikai",
      result: "Reali sąnauda pagal aptarnavimo iškvietimą, realiu laiku",
    },
    relatedSlugs: ["installation", "electricians", "plumbing"],
    relatedArticles: [
      "how-to-track-mileage-for-field-teams",
      "time-tracking-for-field-teams-complete-guide",
    ],
  },

  {
    slug: "electricians",
    name: "Elektrikai",
    headline: "Darbo laiko apskaita elektrikams — valandos pagal projektą, biudžeto kontrolė ir navigacija vienoje programėlėje",
    intro: "Valandos pagal projektą, likusio biudžeto atskaita realiu laiku ir navigacija į objektą vienu mygtuku — sukurta mažoms elektros komandoms, kurioms reikia projekto kontrolės be administracinės naštos.",
    pains: [
      {
        title: "Valandos nesusietos su tinkamu projektu",
        body: "Kai elektrikai per savaitę dirba prie kelių darbų, rankinis valandų priskyrimas dienos pabaigoje veda prie klaidingai priskirtų sąnaudų, sąskaitų ginčų ir darbo užmokesčio, kurio negalite paaiškinti.",
      },
      {
        title: "Biudžeto viršijimai pastebimi per vėlai",
        body: "Be vaizdo realiu laiku, kiek valandų sunaudota palyginti su biudžetuotomis, sužinote, kad projektas viršijo biudžetą, išrašydami sąskaitą — ne tada, kai dar galite reaguoti.",
      },
      {
        title: "Laikas švaistomas ieškant objekto",
        body: "Adresai įvedami į atskirą programėlę, skambučiai į biurą dėl nurodymų, vėlavimai pas klientus. Kiekviena prarasta minutė yra prarasta apmokestinama minutė.",
      },
      {
        title: "Subrangovų valandas sunku suderinti",
        body: "Didesniuose montavimo darbuose subrangovai elektrikai seka savo valandas atskirai. Gauti bendrą darbo sąnaudų vaizdą projekto pabaigoje yra rankinis, klaidoms linkęs darbas.",
      },
    ],
    features: [
      {
        title: "Projekto valandos su likusiu biudžetu realiu laiku",
        body: "Kiekviena dirbta minutė automatiškai susiejama su tinkamu projektu. Atskaita realiu laiku rodo, kiek valandų liko biudžete — Jums ir objekte esančiam darbuotojui.",
        plan: "Project Basic",
      },
      {
        title: "Navigacija į objektą vienu mygtuku",
        body: "Projekto adresas yra programėlėje. Vienas prisilietimas atveria navigaciją — jokios paieškos, jokios atskiros žemėlapių programėlės, jokių skambučių į biurą.",
      },
      {
        title: "Darbuotojų registracija pagal projektą",
        body: "Kiekvienas elektrikas registruojasi tinkamame darbe. Visada žinote, kas kuriame objekte yra šiuo metu.",
        plan: "Project Basic",
      },
      {
        title: "Papildomi darbai registruojami objekte",
        body: "Kai pasikeičia apimtis — netikėta grandinė, papildomos medžiagos — tai registruojama programėlėje su nuotraukomis, prieš komandai išvykstant.",
        plan: "Project Basic",
      },
      {
        title: "Viršvalandžių apskaita su lanksčiu kompensavimu",
        body: "Viršvalandžiai registruojami tą akimirką, kai įvyksta. Darbuotojai pasirenka, ar jie bus apmokami, ar konvertuojami į laisvą laiką.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Registracija su vietos patvirtinimu pagal projektą",
      title: "Biudžeto kontrolė realiu laiku ir registracija su vietos patvirtinimu elektros komandoms",
      body: "Done-it suteikia elektrikams registraciją su vietos patvirtinimu pagal projektą, likusių valandų biudžetą realiu laiku ir darbo užmokesčio eksportą — atitinkant bet kokį vietinį darbo laiko registracijos reikalavimą. Komandoms Belgijos statybvietėse jis taip pat integruojasi su CheckIn@Work.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Elektrikas naviguoja į darbą", body: "Adresas ir apimtis jau yra programėlėje. Vienas prisilietimas atveria navigaciją — jokių skambučių, jokios painiavos." },
      { step: "Valandos automatiškai susiejamos su tinkamu projektu", body: "Registracija objekte susieja kiekvieną dirbtą minutę su tinkamu projektu. Likusių valandų skaitiklis atsinaujina realiu laiku." },
      { step: "Apimties pakeitimai lieka įraše", body: "Papildomi darbai ir medžiagos registruojami objekte, prieš išvykstant. Sąskaita atspindi tai, kas iš tikrųjų įvyko." },
    ],
    stats: [
      { value: "Realiu laiku", label: "likusių valandų rodinys pagal projektą" },
      { value: "0", label: "skambučių į biurą dėl objektų adresų" },
      { value: "1 prisilietimas", label: "kad užsiregistruotumėte bet kuriame objekte — terminalo nereikia" },
    ],
    quote: {
      text: "Realiu laiku žinome, ar projektas viršija biudžetą. Vien tai šiemet apsaugojo mus nuo dviejų blogų staigmenų.",
      who: "Savininkas, elektros įmonė · 5 elektrikai",
      result: "Biudžeto viršijimai pagaunami anksčiau, nei įvyksta",
    },
    relatedSlugs: ["installation", "hvac", "plumbing"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "plumbing",
    name: "Santechnika",
    headline: "Darbo laiko apskaita santechnikos komandoms — greita registracija, tikslus darbo užmokestis ir buvimo įrodymas kiekvienam iškvietimui",
    intro: "Skubūs iškvietimai, keli darbai per dieną, valandos užpildytos iš atminties savaitės pabaigoje — arba visai ne. Done-it tai paverčia vieno prisilietimo darbu kiekviename objekte ir vieno paspaudimo darbo užmokesčiu mėnesio pabaigoje.",
    pains: [
      {
        title: "Iškvietimų valandos atkuriamos iš atminties",
        body: "Su keliais darbais per dieną santechnikai retai turi laiko pildyti tikslius žiniaraščius. Valandos suapvalinamos arba pamirštamos, o darbo užmokestis geriausiu atveju yra įvertinimas.",
      },
      {
        title: "Nėra laiko objekte įrodymo",
        body: "Kai klientas ginčija, kiek truko remontas, nėra objektyvaus įrašo. GPS patvirtintą skaitmeninę registraciją lengva pateikti — popierinį žiniaraštį — ne.",
      },
      {
        title: "Kilometražas tarp darbų nesekamas",
        body: "Kelionės tarp iškvietimų kaupiasi per savaitę ir mėnesį, tačiau be automatinio fiksavimo jos retai teisingai apmokestinamos ar kompensuojamos.",
      },
      {
        title: "Darbo užmokesčio paruošimas lėtas",
        body: "Mėnesio pabaigoje žiniaraščių vaikymasis, klaidų taisymas ir darbo užmokesčio eksporto kūrimas užima laiką, kurio Jūsų administracijos komanda neturėtų leisti.",
      },
    ],
    features: [
      {
        title: "Registracija su vietos patvirtinimu vienu prisilietimu kiekviename iškvietime",
        body: "Atvykimas ir išvykimas registruojami vienu prisilietimu su vietos patvirtinimu. Pakankamai greita skubiems iškvietimams; pakankamai tikslu ginčams.",
        plan: "Time Basic",
      },
      {
        title: "Automatinis pertraukų atėmimas",
        body: "Privalomos pertraukos automatiškai atimamos iš bendro laiko — nieko pildyti, jokių darbo užmokesčio klaidų.",
        plan: "Time Basic",
      },
      {
        title: "Kilometražas fiksuojamas pagal kelionę",
        body: "Konfigūruokite transporto tipus ir kompensacijos tarifus. Kilometrai registruojami automatiškai ir susiejami su tinkamu klientu ar projektu.",
        plan: "Add-on",
      },
      {
        title: "Nebuvimo ir nedarbingumo pažymėjimų valdymas",
        body: "Darbuotojai pateikia nedarbingumo pažymėjimus skaitmeniniu būdu iš programėlės. Sukurkite nebuvimo tipus, kuriuos Jūsų komanda iš tikrųjų naudoja, įskaitant orų sukeltus vėlavimus.",
      },
      {
        title: "Darbo užmokesčio eksportas vienu paspaudimu",
        body: "Visi valandų ir kilometražo duomenys švariai eksportuojami Jūsų darbo užmokesčio paslaugų teikėjui — jokio rankinio paruošimo, jokio Excel.",
        plan: "Time Basic",
      },
    ],
    compliance: {
      label: "Skaitmeninė darbo laiko registracija",
      title: "Greita registracija su vietos patvirtinimu kiekvienam iškvietimui",
      body: "Done-it apima bet kokį vietinį darbo laiko registracijos reikalavimą santechnikos komandoms — registracija su vietos patvirtinimu vienu prisilietimu pagal iškvietimą, automatinis pertraukų atėmimas ir švarus darbo užmokesčio eksportas. Jokių popierinių žiniaraščių, jokio rankinio atkūrimo.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Santechnikas atvyksta į iškvietimą", body: "Vienas prisilietimas registruoja. GPS registruoja vietą ir laiką. Pakankamai greita skubiam darbui." },
      { step: "Išvykimas pažymimas išeinant", body: "Išsiregistravimas uždaro laiko įrašą. Kilometražas iki kito darbo pradedamas registruoti automatiškai." },
      { step: "Mėnesio pabaiga vienu paspaudimu", body: "Visi iškvietimai, valandos ir kilometrai švariai eksportuojami. Darbo užmokesčio paruošimas iš dienos virsta minutėmis." },
    ],
    stats: [
      { value: "1 prisilietimas", label: "kad užsiregistruotumėte — pakankamai greita skubiems iškvietimams" },
      { value: "1 paspaudimas", label: "darbo užmokesčio eksportas Jūsų paslaugų teikėjui" },
      { value: "0", label: "valandų atkurtų iš atminties mėnesio pabaigoje" },
    ],
    quote: {
      text: "Anksčiau praleisdavome rytą mėnesio pabaigoje atkurdami valandas. Dabar tiesiog eksportuojame ir siunčiame. Darbo užmokesčio paslaugų teikėjas gauna jas švarias.",
      who: "Savininkas, santechnikos įmonė · 4 santechnikai",
      result: "Darbo užmokesčio paruošimas nuo valandų iki minučių",
    },
    relatedSlugs: ["hvac", "electricians", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "how-to-track-mileage-for-field-teams",
    ],
  },

  {
    slug: "landscaping",
    name: "Kraštovaizdžio tvarkymas",
    headline: "Darbo laiko apskaita kraštovaizdžio tvarkymo įmonėms — sezoninės komandos, keli objektai ir kilometražas vienoje apžvalgoje",
    intro: "Sezono piko metu — daug objektų, besikeičiančios komandos ir daug kilometrų. Done-it išlaiko kiekvieną valandą, kiekvieną nebuvimą ir kiekvieną kelionę sutvarkytus — kad galėtumėte sutelkti dėmesį į darbą, o ne į popierizmą.",
    pains: [
      {
        title: "Sezonines komandas sudėtinga valdyti",
        body: "Laikini darbuotojai, besikeičiantys grafikai ir kintantys komandos dydžiai daro tikslią valandų apskaitą beveik neįmanomą su popieriumi. Kol suderinate mėnesio pabaigoje, kažko visada trūksta.",
      },
      {
        title: "Valandos išsibarsčiusios po daugelį objektų",
        body: "Komanda, lankanti tris ar keturis objektus per dieną, generuoja valandas, kurias reikia teisingai padalyti. Rankinis įvedimas veda prie klaidingo priskyrimo ir ginčijamo darbo užmokesčio.",
      },
      {
        title: "Orų sukeltus sustojimus sunku dokumentuoti",
        body: "Blogo oro prastova yra reali kraštovaizdžio tvarkymo įvykis. Be skaitmeninio įrašo sustojimų dokumentavimas ir atlygio koregavimas yra lėtas ir klaidoms linkęs.",
      },
      {
        title: "Kilometražas tarp objektų nesekamas",
        body: "Įmonės furgonai, asmeniniai automobiliai ir bendros kelionės visą dieną kerta tarp objektų. Be automatinio fiksavimo tie kilometrai niekada nepatenka į sąnaudų vaizdą.",
      },
    ],
    features: [
      {
        title: "Daugelio objektų komandos apžvalga",
        body: "Matykite visą komandą, visus objektus realiu laiku. Žinokite, kas kur dirba, šiuo metu — ne mėnesio pabaigoje.",
      },
      {
        title: "Pamainų grafikas sezoninėms komandoms",
        body: "Planuokite, kas kur dirba, tvarkykite paskutinės minutės pakeitimus ir siųskite grafiką į komandos telefonus — be skambučio kiekvienam asmeniui.",
        plan: "Time Pro",
      },
      {
        title: "Pasirinktiniai nebuvimo tipai",
        body: "Sukurkite pasirinktinius nebuvimo tipus savo sektoriui: sezoninius orų sustojimus, vaiko priežiūros atostogas, nedarbingumo dienas. Darbuotojai įkelia dokumentaciją skaitmeniniu būdu iš programėlės.",
      },
      {
        title: "Kilometražas su bendros kelionės sekimu",
        body: "Konfigūruokite transporto tipus (įmonės furgonas, asmeninis automobilis, bendros kelionės vairuotojas ar keleivis) su jų tarifais. Kilometrai registruojami automatiškai pagal kelionę.",
        plan: "Add-on",
      },
      {
        title: "Viršvalandžių apskaita su konvertavimu į atostogas",
        body: "Sezono piko viršvalandžiai kaupiasi greitai. Kiekvienas darbuotojas gali pasirinkti būti apmokamas arba kaupti valandas kaip laisvą laiką — sekama skaidriai.",
        plan: "Time Pro",
      },
    ],
    compliance: {
      label: "Skaitmeninė darbo laiko registracija",
      title: "Registracija su vietos patvirtinimu ir nebuvimo sekimas sezoninėms kraštovaizdžio tvarkymo komandoms",
      body: "Done-it suteikia kraštovaizdžio tvarkymo komandoms skaitmeninę darbo laiko registraciją visuose objektuose ir sezoniniams darbuotojams — registracija su vietos patvirtinimu, pasirinktiniai nebuvimo tipai orų sustojimams ir švarus darbo užmokesčio eksportas, atitinkantis bet kokį vietinį reikalavimą.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Komanda registruojasi pirmajame objekte", body: "GPS registruoja atvykimą. Valandos automatiškai padalijamos į tinkamą vietą ir projektą — be rankinio priskyrimo dienos pabaigoje." },
      { step: "Orai sustabdo darbą", body: "Brigadininkas registruoja sustojimą iš programėlės — švarus skaitmeninis įrašas, jokio papildomo popierizmo." },
      { step: "Mėnesio pabaiga: visi objektai, visa komanda, vienas eksportas", body: "Valandos, nebuvimai ir kilometražas visuose objektuose ir kiekvienam komandos nariui švariai eksportuojami darbo užmokesčiui." },
    ],
    stats: [
      { value: "Automatinis", label: "kilometražo fiksavimas — jokio daugiau žurnalo" },
      { value: "Realiu laiku", label: "komandos apžvalga visuose objektuose" },
      { value: "1 paspaudimas", label: "darbo užmokesčio eksportas visiems objektams ir visai komandai" },
    ],
    quote: {
      text: "Sezono pikas anksčiau reiškė darbo užmokesčio chaosą. Dabar mūsų brigadininkas patikrina programėlę, visi suregistruoti, o aš eksportuoju valandas paskutinę mėnesio dieną.",
      who: "Savininkas, kraštovaizdžio tvarkymo įmonė · 12 sezoninių darbuotojų",
      result: "Jokio daugiau mėnesio pabaigos darbo užmokesčio atkūrimo",
    },
    relatedSlugs: ["maintenance", "construction", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },

  {
    slug: "maintenance",
    name: "Priežiūra",
    headline: "Darbo laiko apskaita priežiūros komandoms — pasikartojantys objektų vizitai, sąnauda pagal sutartį ir buvimo įrodymas vienoje programėlėje",
    intro: "Pasikartojančios priežiūros sutartys gyvena arba miršta nuo sąnaudų matomumo. Done-it registruoja kiekvieną vizitą, seka valandas pagal sutarties biudžetą ir suteikia Jums — ir Jūsų klientams — buvimo įrodymą kiekvienam iškvietimui.",
    pains: [
      {
        title: "Nėra sąnaudų matomumo pagal priežiūros sutartį",
        body: "Kai pasikartojančių vizitų valandos nesekamos pagal konkrečią sutartį, nežinote, ar klientas pelningas, iki metų pabaigos peržiūros — o tada jau per vėlu perkainoti.",
      },
      {
        title: "Klientai tikisi įrodymo, kas buvo padaryta",
        body: "Pastatų valdytojai ir savininkai vis dažniau nori lankomumo įrašų: kas atvyko, kada atvyko, ką padarė. Popierinis žurnalas, pateiktas po kelių savaičių, neatitinka SLA reikalavimų.",
      },
      {
        title: "Pasikartojančius vizitus sunku planuoti ir registruoti",
        body: "Besikeičiančios pamainos daugelyje pasikartojančių objektų — su darbuotojais, kurie keičia vieni kitus — sukuria planavimo ir valandų sekimo galvosūkį, kurio rankiniai procesai negali patikimai išspręsti.",
      },
      {
        title: "Orų sustojimai veikia ir lauko priežiūrą",
        body: "Teritorijų priežiūra, fasadų priežiūra ir lauko įrenginių darbai susiduria su orų sukeltais sustojimais. Norint juos teisingai dokumentuoti, reikia skaitmeninio pėdsako.",
      },
    ],
    features: [
      {
        title: "Sutartimi pagrįsta sąnaudų apskaita",
        body: "Kiekviena priežiūros sutartis yra projektas. Valandos automatiškai registruojamos jai, suteikdamos sąnaudų skaičių realiu laiku ir likusių valandų atskaitą kiekvienam klientui.",
        plan: "Project Basic",
      },
      {
        title: "GPS patvirtintas buvimo įrodymas",
        body: "Laiku pažymėtos, su vietos patvirtinimu atliktos registracijos yra neginčijamas įrašas, kas buvo objekte, kada ir kiek laiko — paruoštas dalintis su klientais pareikalavus.",
      },
      {
        title: "Atnaujinimai su nuotraukomis iš objekto",
        body: "Darbuotojai praneša apie problemas, atliktas užduotis ar papildomus darbus tiesiai iš programėlės — su tekstu ir nuotraukomis. Klientai gauna įrašą realiu laiku; Jūs turite dokumentaciją sąskaitoms.",
        plan: "Project Basic",
      },
      {
        title: "Pasikartojantis pamainų grafikas",
        body: "Planuokite pasikartojančius vizitus visose sutartyse, priskirkite darbuotojus ir tvarkykite pakeitimus — be skambučio kiekvienam asmeniui.",
        plan: "Time Pro",
      },
      {
        title: "Nebuvimo tipai lauko priežiūrai",
        body: "Konfigūruokite orų sustojimus ir kitus nebuvimo tipus. Darbuotojai registruoja juos iš programėlės; dokumentacija įkeliama skaitmeniniu būdu.",
      },
    ],
    compliance: {
      label: "Sutartimi pagrįsta darbo laiko apskaita",
      title: "Sąnaudų matomumas realiu laiku pagal priežiūros sutartį",
      body: "Done-it suteikia priežiūros komandoms GPS patvirtintą buvimo įrodymą pagal sutartį, pasirinktinius nebuvimo tipus ir sąnaudų vaizdą realiu laiku pagal pasikartojantį klientą — atitinkant bet kokį vietinį darbo laiko registracijos reikalavimą be papildomos įrangos.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Darbuotojas atvyksta į priežiūros objektą", body: "Registracija su vietos patvirtinimu registruoja atvykimo laiką ir susieja valandas su tinkama sutartimi. Buvimo įrodymas sukuriamas automatiškai." },
      { step: "Užduotys atliktos, problemos užregistruotos", body: "Atliktas darbas ir bet kokios problemos pranešamos iš programėlės su nuotraukomis. Klientai gali tiksliai matyti, kas buvo padaryta." },
      { step: "Sąnauda pagal sutartį matoma iškart", body: "Valandos kaupiasi pagal sutarties biudžetą realiu laiku. Žinote maržą prieš sutarties atnaujinimą." },
    ],
    stats: [
      { value: "Realiu laiku", label: "sąnauda pagal priežiūros sutartį" },
      { value: "GPS", label: "buvimo įrodymas paruoštas klientams pareikalavus" },
      { value: "0", label: "sutarčių atnaujinimų nežinant savo maržos" },
    ],
    quote: {
      text: "Galime pasakyti pastatų valdytojui tiksliai, kas buvo objekte ir kada, per dvi minutes. Toks įrodymo lygis pakeitė tai, kaip klientai mus mato.",
      who: "Operacijų vadovas, priežiūros įmonė · 8 darbuotojai",
      result: "Klientų ginčai sumažėjo iki nulio",
    },
    relatedSlugs: ["cleaning", "landscaping", "field-service"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
  {
    slug: "retail",
    name: "Parduotuvės ir prekyba",
    headline: "Darbo laiko apskaita parduotuvėms — pamainos žymėjimas, pavadavimai ir atlyginimams paruoštas eksportas vienoje programėlėje",
    intro: "Atidarymo pamainos, vėlyvos pamainos ir nuolat kažkas ką nors pavaduoja. Done-it kiekvienai parduotuvei suteikia tvarkingą skaitmeninį įrašą, kas kurią pamainą dirbo — pažymėta savo telefone ir paruošta atlyginimams mėnesio pabaigoje.",
    pains: [
      {
        title: "Pamainų mainai nepasiekia atlyginimų",
        body: "Vienas pavaduoja sergantį kolegą, kitas uždarydamas užtrunka valanda ilgiau. Tai lieka užrašyta ant lapelio arba niekur — ir atlyginimai klaidingi dar nepradėjus skaičiuoti.",
      },
      {
        title: "Kelios parduotuvės, vienas atlyginimų skaičiavimas",
        body: "Darbuotojai keliauja tarp padalinių, dirbantieji ne visą dieną dengia dvi vietas. Valandų rinkimas po parduotuvę ir jų sujungimas rankomis kainuoja dieną kas mėnesį.",
      },
      {
        title: "Pertraukos ir viršvalandžiai — spėjimas",
        body: "Privalomos pertraukos nuskaičiuojamos iš atminties, o papildomos valandos suderinamos neoficialiai — būtent apie tai patikrinimas klausia pirmiausia.",
      },
    ],
    features: [
      {
        title: "Pamainos žymėjimas prekybos salėje",
        body: "Vienas bakstelėjimas savo telefone pamainos pradžioje. Jokio terminalo prie kasos, jokios bendros kortelės, jokio segtuvo prie sandėlio durų.",
      },
      {
        title: "Valandos po parduotuvę, darbuotojai tarp padalinių",
        body: "Kiekviena parduotuvė — atskiras projektas. Pavaduojantis kitame padalinyje žymimas būtent tam padaliniui: matote valandas ten, kur jos iš tikrųjų atidirbtos.",
      },
      {
        title: "Pertraukos nuskaičiuojamos automatiškai",
        body: "Nustatykite pertraukų taisyklę kartą — ji galios kiekvienai pamainai. Viršvalandžiai fiksuojami tą pačią akimirką, o kiekvienas darbuotojas pasirenka apmokėjimą arba laisvą laiką.",
      },
    ],
    compliance: {
      label: "Pamaininė laiko apskaita",
      title: "Pagrįstas kiekvienos atidirbtos pamainos įrašas",
      body: "Done-it saugo objektyvų skaitmeninį kiekvieno atvykimo ir išvykimo įrašą su pertraukomis ir viršvalandžiais — būtent tokį, kokio prašo auditas ar ginčas dėl atlyginimo.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Darbuotojai pažymi pamainos pradžią", body: "Vienas bakstelėjimas telefone prasidedant pamainai. Pavadavimas kitame padalinyje žymimas tam padaliniui." },
      { step: "Matote, kas dirba kiekvienoje parduotuvėje", body: "Gyvas vaizdas visuose padaliniuose — neužpildytą atidarymo pamainą pastebite dar prieš atidarant duris." },
      { step: "Atlyginimai — vienu eksportu", body: "Valandos, pertraukos ir viršvalandžiai pagal darbuotoją ir parduotuvę, paruošti jūsų atlyginimų paslaugų teikėjui." },
    ],
    stats: [
      { value: "1 bakstelėjimas", label: "ir pamaina pažymėta — jokio terminalo prie kasos" },
      { value: "Gyvai", label: "matote, kas dirba kiekvienoje parduotuvėje" },
      { value: "0", label: "pamainų mainų, pamestų pakeliui į atlyginimus" },
    ],
    quote: {
      text: "Dvi parduotuvės ir daug dirbančiųjų ne visą dieną. Anksčiau pusė pavadavimo pamainų būdavo suskaičiuojama iš atminties. Dabar kiekviena valanda priskirta tinkamai parduotuvei, o atlyginimai užtrunka kelias minutes.",
      who: "Savininkas, mažmeninė prekyba · 11 darbuotojų",
      result: "Pasiruošimas atlyginimams — nuo dienos iki mažiau nei valandos",
    },
    relatedSlugs: ["hospitality", "beauty", "offices"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "offices",
    name: "Biurai ir profesinės paslaugos",
    headline: "Darbo laiko apskaita biurams — valandos pagal klientą ir projektą be skaičiuoklių",
    intro: "Buhalteriai, notarai, architektai, agentūros ir konsultantai apmoka savo laiką — arba bent jau turi žinoti, kur jis dingo. Done-it fiksuoja valandas tinkamam klientui ir projektui: prie darbo stalo arba kelyje.",
    pains: [
      {
        title: "Apmokamos valandos atkuriamos penktadienį",
        body: "Laikas, užrašytas savaitės pabaigoje, yra spėjamas laikas. Ištisos valandos tyliai dingsta tarp darbo ir sąskaitos.",
      },
      {
        title: "Nežinia, kiek iš tikrųjų kainuoja klientas",
        body: "Kaina sutarta prieš kelis mėnesius. Ar darbas vis dar telpa į ją, paaiškėja tik kai kas nors susumuoja — dažniausiai per vėlai.",
      },
      {
        title: "Hibridinis darbas palieka spragų įrašuose",
        body: "Dalis komandos biure, dalis namuose, dalis pas klientą. Buvimas darbe nustoja būti faktu ir tampa atkūrimu.",
      },
    ],
    features: [
      {
        title: "Valandos pagal klientą ir projektą",
        body: "Kiekvienas įrašas patenka į projektą, todėl valandas pagal klientą matote nieko neeksportuodami ir nieko neklausdami.",
      },
      {
        title: "Veikia prie stalo ir išvykoje",
        body: "Ta pati programėlė telefone ar naršyklėje. Vizitas pas klientą įskaitomas kaip darbas tam klientui, o jei norite — kartu su kelione.",
      },
      {
        title: "Viršvalandžiai ir atostogos vienoje vietoje",
        body: "Papildomos valandos fiksuojamos iškart, atostogos prašomos ir tvirtinamos programėlėje — jokios lygiagrečios skaičiuoklės, kas ir kada nedirba.",
      },
    ],
    compliance: {
      label: "Laiko apskaita pagal klientą",
      title: "Objektyvus atidirbtų valandų įrašas",
      body: "Done-it saugo skaitmeninį, klastojimą atskleidžiantį kiekvieno darbuotojo darbo laiko įrašą — pagrindą ir atlyginimams, ir pagrįstai kliento sąskaitai.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Sukurkite kliento projektą", body: "Nustatykite kartą: klientą ir, jei dirbate pagal biudžetą, suplanuotas valandas." },
      { step: "Komanda fiksuoja laiką jame", body: "Prie stalo arba telefone. Kiekviena valanda patenka klientui, o ne į kažkieno atmintį." },
      { step: "Matykite valandas pagal klientą, eksportuokite atlyginimams", body: "Gyvos projektų sumos ir tvarkingas eksportas atlyginimams ar sąskaitoms mėnesio pabaigoje." },
    ],
    stats: [
      { value: "Pagal klientą", label: "valandos, matomos dar vykstant darbui" },
      { value: "Biure ar kelyje", label: "tas pats įrašas telefone ir naršyklėje" },
      { value: "1 eksportas", label: "atlyginimams ir sąskaitoms" },
    ],
    quote: {
      text: "Anksčiau kas penktadienį atkurdavome visą savaitę. Dabar valandos jau priskirtos tinkamam klientui, ir matau, kurios bylos suryja visą honorarą.",
      who: "Partneris, buhalterinės apskaitos įmonė · 8 darbuotojai",
      result: "Kas mėnesį atgautos apmokamos valandos",
    },
    relatedSlugs: ["retail", "healthcare", "wellness"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "beauty",
    name: "Salonai ir kūno priežiūra",
    headline: "Darbo laiko apskaita salonams — pamainos, ne viso etato valandos ir atlyginimai be popierinio žurnalo",
    intro: "Kirpyklos, barberiai, nagų ir grožio studijos dirba pamainomis ir su ne viso etato sutartimis. Done-it fiksuoja, kas ir kada dirbo, automatiškai nuskaičiuoja pertraukas ir pateikia buhalterijai tvarkingą failą.",
    pains: [
      {
        title: "Ne viso etato sutartys, begalė variantų",
        body: "Vieną savaitę trys dienos, kitą keturios, šeštadienis iškeistas į antradienį. Sutartos ir atidirbtos valandos greitai išsiskiria.",
      },
      {
        title: "Klientų žurnalas nėra darbo laiko apskaita",
        body: "Registracijos rodo, kas buvo užsakyta, o ne kas dirbo. Kai klausia buhalterija ar inspekcija, rezervacijų sistema yra netinkamas atsakymas.",
      },
      {
        title: "Papildomos valandos suderinamos neoficialiai",
        body: "Pasilikimas paskutiniam klientui prisimenamas, pažadamas ir kartais pamirštamas. Būtent tokia spraga virsta ginču.",
      },
    ],
    features: [
      {
        title: "Pamainos žymėjimas salone",
        body: "Vienas bakstelėjimas savo telefone pamainos pradžioje — jokio bendro terminalo prie registratūros, jokio parašų sąrašo prie veidrodžio.",
      },
      {
        title: "Pertraukų ir ne viso etato taisyklės — kartą",
        body: "Nustatykite pertraukų taisyklę ir standartines valandas vieną kartą; jos galios kiekvienai pamainai, su išimtimis tiems, kurių sutartis kitokia.",
      },
      {
        title: "Viršvalandžiai — apmokėti arba atsiimti",
        body: "Papildomos valandos fiksuojamos tą pačią akimirką, o kiekvienas darbuotojas renkasi apmokėjimą arba laisvą laiką — sutvarkoma programėlėje, o ne iš atminties.",
      },
    ],
    compliance: {
      label: "Skaitmeninė laiko apskaita",
      title: "Įrašas, kuris galioja ir be registracijų žurnalo",
      body: "Done-it fiksuoja faktinį kiekvieno darbuotojo darbo laiką su pertraukomis ir viršvalandžiais — atskirai nuo rezervacijų ir pagrįstai, kai tikrinama.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Darbuotojai pažymi pamainos pradžią", body: "Vienas bakstelėjimas atėjus. Pertraukos nuskaičiuojamos pagal kartą nustatytą taisyklę." },
      { step: "Matote dieną tokią, kokia ji vyksta", body: "Kas dirba, kas per pertrauką, kas pasiliko ilgiau — gyvai ir nieko neklausiant." },
      { step: "Buhalterija gauna tvarkingą failą", body: "Atidirbtos valandos, pertraukos ir viršvalandžiai pagal darbuotoją, eksportuoti jūsų atlyginimų paslaugų teikėjui." },
    ],
    stats: [
      { value: "1 bakstelėjimas", label: "ir pamaina pažymėta — nieko bendro, nieko popieriuje" },
      { value: "Automatiškai", label: "nuskaičiuojamos pertraukos kiekvienoje pamainoje" },
      { value: "0", label: "vėlyvų valandų, suskaičiuotų iš atminties" },
    ],
    quote: {
      text: "Visi dirba ne visą etatą ir kiekviena savaitė kitokia. Programėlė tiesiog viską užfiksuoja — nustojau kas mėnesį perrašinėti skaičiuoklę.",
      who: "Savininkė, kirpykla · 6 darbuotojai",
      result: "Mėnesio pabaigoje nebelieka ginčų dėl valandų",
    },
    relatedSlugs: ["wellness", "retail", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "wellness",
    name: "Sveikata ir sveikatingumas",
    headline: "Darbo laiko apskaita sporto klubams, studijoms ir SPA — pamainos, treniruotės ir atlyginimai vienoje programėlėje",
    intro: "Sporto klubai, fitneso studijos, kineziterapijos kabinetai ir SPA dirba ilgas valandas su besikeičiančiomis pamainomis. Done-it fiksuoja, kas kurią pamainą dengė, automatiškai nuskaičiuoja pertraukas ir tvarkingai eksportuoja duomenis atlyginimams.",
    pains: [
      {
        title: "Ilgas darbo laikas, nuolat besikeičiantys pavadavimai",
        body: "Ankstyvos pamainos, vakarinės treniruotės ir savaitgaliai, kuriuos dengia visą ir ne visą etatą dirbantys bei laisvai samdomi darbuotojai. Grafikas yra planas, o ne apskaita.",
      },
      {
        title: "Treniruočių ir salės valandos susilieja",
        body: "Instruktorius veda dvi treniruotes, o tarp jų budi registratūroje. Kad apmokėtumėte teisingai, reikia žinoti, kas iš tikrųjų atidirbta, o ne kas buvo suplanuota.",
      },
      {
        title: "Kelios vietos, viena administracija",
        body: "Darbuotojai juda tarp studijų. Kas mėnesį sujungti jų valandas rankomis lėta ir klaidinga.",
      },
    ],
    features: [
      {
        title: "Pamainos žymėjimas kiekvienoje studijoje",
        body: "Vienas bakstelėjimas telefone toje vietoje, kurioje dirbama. Pavadavimas kitoje studijoje patenka į tos studijos valandas.",
      },
      {
        title: "Gyvas vaizdas, kas dirba",
        body: "Matykite salę visose vietose realiu laiku — neužpildyta ankstyva pamaina matoma dar prieš atidarant duris, o ne po to.",
      },
      {
        title: "Pertraukos ir viršvalandžiai — sutvarkyti",
        body: "Pertraukų taisyklės taikomos automatiškai, papildomos valandos fiksuojamos iškart, o kiekvienas darbuotojas renkasi apmokėjimą arba laisvą laiką.",
      },
    ],
    compliance: {
      label: "Pamainų apskaita keliose vietose",
      title: "Objektyvus įrašas kiekvienai pamainai ir vietai",
      body: "Done-it saugo skaitmeninį darbo laiko įrašą pagal darbuotoją ir vietą — pagrindą teisingam apmokėjimui ir bet kokiam atidirbtų valandų patikrinimui.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Darbuotojai pažymi pamainą studijoje", body: "Vienas bakstelėjimas pamainos pradžioje, toje vietoje, kurioje dirbama." },
      { step: "Pavadavimai matomi gyvai", body: "Kas ir kurioje vietoje dirba dabar, kad spragas užpildytumėte anksčiau, nei pastebės klientai." },
      { step: "Vienas eksportas atlyginimams", body: "Valandos, pertraukos ir viršvalandžiai pagal darbuotoją ir studiją, paruošti atlyginimų paslaugų teikėjui." },
    ],
    stats: [
      { value: "Pagal studiją", label: "valandos, net kai darbuotojai pavaduoja kitur" },
      { value: "Gyvai", label: "matote salę visose vietose" },
      { value: "Automatiškai", label: "pertraukos ir viršvalandžiai, be skaičiavimų ranka" },
    ],
    quote: {
      text: "Dvi studijos, ilgas darbo laikas ir daug pavadavimo pamainų. Dabar valandos patenka į tinkamą vietą, o atlyginimams užtenka vieno failo.",
      who: "Vadovas, fitneso studija · 14 darbuotojų",
      result: "Grafikas ir atlyginimai pagaliau sutampa",
    },
    relatedSlugs: ["beauty", "healthcare", "hospitality"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "hospitality",
    name: "Maitinimas ir apgyvendinimas",
    headline: "Darbo laiko apskaita kavinėms, restoranams ir viešbučiams — pamainos, pertraukos ir viršvalandžiai, apskaityti sąžiningai",
    intro: "Suskaidytos pamainos, vėlyvi uždarymai ir šeštadienis, kuris užsitęsia. Done-it fiksuoja valandas tokias, kokios iš tikrųjų atidirbtos, nuskaičiuoja pertraukas pagal jūsų taisyklę ir pateikia buhalterijai vieną tvarkingą failą.",
    pains: [
      {
        title: "Pamaina niekada nesibaigia taip, kaip rašo grafikas",
        body: "Aptarnavimas užsitęsia, uždarymas atima dar pusvalandį. Tos minutės yra tikras darbas, o skaičiavimas iš atminties — ginčų pradžia.",
      },
      {
        title: "Suskaidytas pamainas sunku užfiksuoti",
        body: "Pietų aptarnavimas, pertrauka, tada vakarienė. Popieriuje tai virsta vienu ilgu bloku arba dviem spėjimais — nė vienas variantas nėra tiesa.",
      },
      {
        title: "Didelė kaita, nuolatinis naujokų mokymas",
        body: "Kiekvieną sezoną nauji darbuotojai. Bet kokia sistema, kuriai reikia mokymų, neišgyvens pirmos įtemptos savaitės.",
      },
    ],
    features: [
      {
        title: "Žymėjimas kiekvieno aptarnavimo pradžioje ir pabaigoje",
        body: "Vienas bakstelėjimas kiekvieno bloko pradžioje ir pabaigoje, kad suskaidytos pamainos būtų užfiksuotos tokios, kokios buvo — o ne sulietos į vieną.",
      },
      {
        title: "Pertraukos pagal jūsų taisyklę",
        body: "Privalomos pertraukos nuskaičiuojamos automatiškai, su išimtimis tiems, kurių sutartis to reikalauja. Jokių skaičiavimų ranka mėnesio pabaigoje.",
      },
      {
        title: "Viršvalandžiai sutvarkomi programėlėje",
        body: "Papildomas pusvalandis po uždarymo fiksuojamas tą pačią akimirką ir apmokamas arba atsiimamas laisvu laiku — renkasi pats darbuotojas.",
      },
    ],
    compliance: {
      label: "Suskaidytų pamainų apskaita",
      title: "Pagrįstas valandų įrašas komandoje, kurioje didelė kaita",
      body: "Done-it saugo objektyvų skaitmeninį kiekvienos pamainos, pertraukos ir papildomos valandos įrašą — būtent tai, ko prašo atlyginimų patikra ar darbo inspekcija.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Darbuotojai pažymi aptarnavimo pradžią", body: "Vienas bakstelėjimas bloko pradžioje ir toks pat pabaigoje. Suskaidytos pamainos lieka suskaidytos." },
      { step: "Matote salę gyvai", body: "Kas dirba šio vakaro aptarnavime, kas dar per pertrauką, kas pasiliko po uždarymo." },
      { step: "Buhalterija gauna tikras valandas", body: "Atidirbtas laikas, pertraukos ir viršvalandžiai pagal darbuotoją, eksportuoti vienu failu." },
    ],
    stats: [
      { value: "Suskaidytos pamainos", label: "užfiksuotos kaip du blokai, o ne vienas spėjimas" },
      { value: "Automatiškai", label: "nuskaičiuojamos pertraukos pagal jūsų taisyklę" },
      { value: "1 min.", label: "naujam darbuotojui pradėti — tai jo paties telefonas" },
    ],
    quote: {
      text: "Uždarymas visada užsitęsia. Dabar tos minutės fiksuojamos, o ne ginčijamos, ir darbuotojai gali jas atsiimti laisvu laiku.",
      who: "Savininkas, restoranas · 17 darbuotojų",
      result: "Mažiau ginčų, greitesni atlyginimai",
    },
    relatedSlugs: ["retail", "wellness", "beauty"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "reducing-payroll-admin-for-field-service-companies",
    ],
  },
  {
    slug: "healthcare",
    name: "Sveikatos ir slaugos paslaugos",
    headline: "Darbo laiko apskaita slaugos komandoms — pamainos, vizitai namuose ir kvalifikacijos viename įraše",
    intro: "Kabinetai, slaugos namuose komandos ir globos namai dirba pagal pamainas, vizitus ir griežtus apskaitos reikalavimus. Done-it registruoja atidirbtą laiką pagal pamainą ir pagal vizitą, o darbuotojų dokumentus laiko ten, kur juos rasite.",
    pains: [
      {
        title: "Vizitai ir pamainos — atskirose sistemose",
        body: "Vizitai namuose vienoje programoje, grafiko valandos kitoje, o buhalterija gauna trečią versiją. Jų suderinimas virsta mėnesiniu darbu.",
      },
      {
        title: "Naktinės ir savaitgalio valandos įkainotos klaidingai",
        body: "Už nepatogų laiką mokami kitokie tarifai. Kai apskaita apytikslė, toks pat ir atlyginimas — o pataisymas ateina vėliau.",
      },
      {
        title: "Pažymėjimai tyliai baigia galioti",
        body: "Kvalifikacijos ir pažymėjimai slaugoje yra svarbūs, tačiau guli aplanke, kurio niekas neatverčia, kol nepaklausia inspekcija.",
      },
    ],
    features: [
      {
        title: "Žymėjimas pagal pamainą arba pagal vizitą",
        body: "Vienas bakstelėjimas pamainos pradžioje arba prie kiekvieno kliento adreso teikiant priežiūrą namuose — su vietos patvirtinimu ten, kur reikia įrodymo.",
      },
      {
        title: "Valandos suskirstytos taip, kaip už jas mokate",
        body: "Naktinės, savaitgalio ir įprastos valandos fiksuojamos atskirai, todėl eksportas atlyginimams jau atspindi, kaip darbas iš tikrųjų apmokamas.",
      },
      {
        title: "Darbuotojų dokumentai vienoje vietoje",
        body: "Sutartys, pažymėjimai ir asmens dokumentai saugomi prie to žmogaus, kuriam priklauso — randami per kelias sekundes, kai kas nors jų paprašo.",
      },
    ],
    compliance: {
      label: "Pamainų ir vizitų apskaita",
      title: "Įrašai, kurie atlaiko patikrinimą",
      body: "Done-it saugo skaitmeninį, objektyvų kiekvieno darbuotojo darbo laiko įrašą — valandas, pertraukas ir nepatogaus laiko atskyrimą — kartu su kiekvienam žmogui priklausančiais dokumentais.",
      slug: "time-tracking-for-field-teams-complete-guide",
    },
    workflow: [
      { step: "Pažymėkite pamainos ar vizito pradžią", body: "Vienas bakstelėjimas vietoje arba kliento adresu, su vietos patvirtinimu ten, kur reikia įrodymo." },
      { step: "Valandos patenka į tinkamą kategoriją", body: "Įprastos, naktinės ir savaitgalio valandos fiksuojamos atskirai tą pačią akimirką." },
      { step: "Eksportas atlyginimams", body: "Vienas tvarkingas failas už laikotarpį, jau suskirstytas taip, kaip mokate." },
    ],
    stats: [
      { value: "Pagal vizitą", label: "arba pagal pamainą — kaip dirba jūsų komanda" },
      { value: "Atskirai", label: "naktinės ir savaitgalio valandos, fiksuotos kaip atidirbtos" },
      { value: "Sutvarkyta", label: "pažymėjimai saugomi prie kiekvieno darbuotojo" },
    ],
    quote: {
      text: "Vizitai namuose ir grafiko pamainos pagaliau yra tame pačiame įraše. Atlyginimai nustojo būti derinimo pratybomis.",
      who: "Koordinatorė, slaugos namuose komanda · 22 darbuotojai",
      result: "Vienas įrašas vietoj trijų",
    },
    relatedSlugs: ["wellness", "offices", "cleaning"],
    relatedArticles: [
      "time-tracking-for-field-teams-complete-guide",
      "gps-tracking-field-workers-privacy-gdpr",
    ],
  },
];
